// Piers and berths are grouped under their city so the port filter shows one entry per port
const PORT_GROUPS = [
  { port: 'Warnemünde', match: /warnem/i },
  { port: 'Rostock', match: /rostock/i },
  { port: 'Kiel', match: /kiel/i },
  { port: 'Hamburg', match: /hamburg|hafencity|altona|steinwerder|baakenh/i },
  { port: 'Bremerhaven', match: /bremerhaven/i },
  { port: 'Bremen', match: /bremen/i },
  { port: 'Berlin', match: /berlin|charlottenburg|potsdamer platz|tempelhof/i },
  { port: 'Wismar', match: /wismar/i },
  { port: 'Travemünde', match: /travem/i },
  { port: 'Lübeck', match: /lübeck|lubeck|burgtorkai/i },
  { port: 'Binz', match: /binz/i },
  { port: 'Sassnitz', match: /sassnitz|mukran/i },
  { port: 'Flensburg', match: /flensburg/i },
  { port: 'Cuxhaven', match: /cuxhaven/i },
  { port: 'Borkum', match: /borkum/i },
  { port: 'Helgoland', match: /helgoland/i }
]


const getPortGroup = (pier) => {
  if (!pier) return ''
  return PORT_GROUPS.find(group => group.match.test(pier))?.port || pier.trim()
}

// Internal Time Change events and trainings, not cruise line business
const EXCLUDED_CRUISE_LINES = ['Time Change GmbH', 'Time Change Training']

export const transformJsonToRows = (json) => {
  if (!json?.data?.office) return []

  const rows = []
  
  json.data.office.forEach(office => {
    office?.ship_calls?.forEach(call => {
      const shipName = call?.cruise?.ship?.name || 'Unknown'
      const cruiseLine = call?.cruise?.ship?.line?.name || 'Unknown'
      if (EXCLUDED_CRUISE_LINES.includes(cruiseLine)) return

      const pier = call?.port?.name?.trim() || ''
      const port = getPortGroup(pier)
      
      call?.tours?.forEach(tour => {
        const tourDetails = extractTourDetails(tour)
        
        rows.push({
          name: tour?.name?.trim() || '—',
          code: tour?.code?.trim() || '—',
          date: tour?.date || '',
          year: tour?.date?.split('-')[0] || '',
          start_time: tour?.start_time || '',
          cruiseLine,
          shipName,
          port,
          pier,
          participants: tourDetails.participants,
          escortName: tourDetails.escortName,
          guideName: tourDetails.guideName,
          staff: tourDetails.staff
        })
      })
    })
  })
  
  return rows
}

const extractTourDetails = (tour) => {
  const activities = tour?.activities || []
  const guides = []
  let escort = null

  // Collect all guides and escort data
  activities.forEach(activity => {
    const actName = activity.name || ''
    const participants = calculateParticipants(activity.attendances || [])
    const hasValidData = hasValidParticipants(activity.attendances || [])
    
    if (actName.includes('Guide')) {
      const guideName = actName.split('Guide: ')[1]?.trim() || '—'
      guides.push({
        name: guideName,
        participants,
        hasValidData
      })
    } else if (actName.includes('Escort')) {
      const escortName = actName.split('Escort: ')[1]?.trim() || '—'
      escort = {
        name: escortName,
        participants,
        hasValidData
      }
    }
  })

  // Priority for participant count:
  let finalParticipants = 0
  let finalGuideName = '—'
  let finalEscortName = '—'

  // 1. First priority: Guides with valid data
  const validGuides = guides.filter(guide => guide.hasValidData)
  if (validGuides.length > 0) {
    // Use the guide with highest participant count
    const bestGuide = validGuides.reduce((best, current) => 
      current.participants > best.participants ? current : best, validGuides[0]
    )
    finalParticipants = bestGuide.participants
    finalGuideName = bestGuide.name
  }
  // 2. Second priority: Escort with valid data (if no valid guides)
  else if (escort && escort.hasValidData) {
    finalParticipants = escort.participants
    finalEscortName = escort.name
  }
  // 3. Fallback: guide/escort voucher not submitted, so use the highest count
  //    from any other activity (e.g. FnB, Inside, Transport vouchers)
  else {
    activities.forEach(activity => {
      const participants = calculateParticipants(activity.attendances || [])
      if (participants > finalParticipants) {
        finalParticipants = participants
      }
    })
  }

  // Set guide names (all guides, comma separated)
  const allGuideNames = guides.map(g => g.name).filter(name => name !== '—')
  if (allGuideNames.length > 0) {
    finalGuideName = allGuideNames.join(', ')
  }

  // Staff is repeated on every activity of a tour, so take the highest count instead of summing
  const staff = activities.reduce((max, activity) =>
    Math.max(max, getAttendanceQuantity(activity.attendances || [], 'staff')), 0
  )
  return {
    participants: finalParticipants,
    escortName: escort?.name || '—',
    guideName: finalGuideName,
    staff
  }
}

const getAttendanceQuantity = (attendances, type) => {
  return attendances.reduce((sum, attendance) =>
    attendance?.type === type ? sum + (attendance.quantity || 0) : sum, 0
  )
}

const hasValidParticipants = (attendances) => {
  return attendances.some(att => 
    ['adults', 'children'].includes(att?.type) && 
    att?.quantity !== null && 
    att?.quantity !== undefined && 
    att?.quantity > 0
  )
}

const calculateParticipants = (attendances) => {
  return attendances.reduce((sum, attendance) => {
    if (['adults', 'children'].includes(attendance?.type) && 
        attendance?.quantity !== null && 
        attendance?.quantity !== undefined) {
      return sum + (attendance.quantity || 0)
    }
    return sum
  }, 0)
}